import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, MessageCircle, Phone, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/ai-whatsapp-integration.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const IntegrateAIWhatsApp = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.integrateAIWhatsApp.metaTitle')}</title>
        <meta name="description" content={t('blog.integrateAIWhatsApp.metaDescription')} />
        <meta name="keywords" content="AI WhatsApp integration, AI webchat support, omnichannel AI customer support, AI customer service automation, integrate AI with messaging" />
        <link rel="canonical" href="https://call24x7.ai/blog/integrate-ai-customer-support-whatsapp" />
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:title" content={t('blog.integrateAIWhatsApp.title')} />
        <meta property="article:published_time" content="2025-12-05" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.integrateAIWhatsApp.title'),
            "description": t('blog.integrateAIWhatsApp.metaDescription'),
            "author": {"@type": "Organization", "name": "Call24x7.AI"},
            "publisher": {"@type": "Organization", "name": "Call24x7.AI"},
            "datePublished": "2025-12-05",
            "mainEntityOfPage": "https://call24x7.ai/blog/integrate-ai-customer-support-whatsapp"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.integrateAIWhatsApp.title')}
        category={t('blog.categories.integration')}
        date="December 5, 2025"
        readTime={`8 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="AI WhatsApp and webchat integration"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.integrateAIWhatsApp.intro')}
        </p>

        <div className="grid md:grid-cols-3 gap-4 my-8">
          <Card className="p-4 text-center">
            <Phone className="w-10 h-10 text-primary mx-auto mb-2" />
            <div className="font-bold">{t('blog.integrateAIWhatsApp.channel1')}</div>
            <p className="text-sm text-muted-foreground">{t('blog.integrateAIWhatsApp.channel1Desc')}</p>
          </Card>
          <Card className="p-4 text-center">
            <MessageCircle className="w-10 h-10 text-primary mx-auto mb-2" />
            <div className="font-bold">{t('blog.integrateAIWhatsApp.channel2')}</div>
            <p className="text-sm text-muted-foreground">{t('blog.integrateAIWhatsApp.channel2Desc')}</p>
          </Card>
          <Card className="p-4 text-center">
            <Globe className="w-10 h-10 text-primary mx-auto mb-2" />
            <div className="font-bold">{t('blog.integrateAIWhatsApp.channel3')}</div>
            <p className="text-sm text-muted-foreground">{t('blog.integrateAIWhatsApp.channel3Desc')}</p>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.integrateAIWhatsApp.whyOmnichannelTitle')}</h2>
        <p>{t('blog.integrateAIWhatsApp.whyOmnichannelDesc')}</p>

        <div className="space-y-3 my-6">
          {[
            t('blog.integrateAIWhatsApp.benefit1'),
            t('blog.integrateAIWhatsApp.benefit2'),
            t('blog.integrateAIWhatsApp.benefit3'),
            t('blog.integrateAIWhatsApp.benefit4')
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.integrateAIWhatsApp.step1Title')}</h2>
        <p>{t('blog.integrateAIWhatsApp.step1Intro')}</p>
        <ul className="list-disc list-inside space-y-2 my-4">
          <li>{t('blog.integrateAIWhatsApp.step1Item1')}</li>
          <li>{t('blog.integrateAIWhatsApp.step1Item2')}</li>
          <li>{t('blog.integrateAIWhatsApp.step1Item3')}</li>
          <li>{t('blog.integrateAIWhatsApp.step1Item4')}</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.integrateAIWhatsApp.step2Title')}</h2>
        <p>{t('blog.integrateAIWhatsApp.step2Intro')}</p>
        <ol className="list-decimal list-inside space-y-2 my-4">
          <li>{t('blog.integrateAIWhatsApp.step2Item1')}</li>
          <li>{t('blog.integrateAIWhatsApp.step2Item2')}</li>
          <li>{t('blog.integrateAIWhatsApp.step2Item3')}</li>
          <li>{t('blog.integrateAIWhatsApp.step2Item4')}</li>
        </ol>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.integrateAIWhatsApp.step3Title')}</h2>
        <p>{t('blog.integrateAIWhatsApp.step3Intro')}</p>
        <div className="bg-muted p-4 rounded-lg my-4">
          <code className="text-sm">
            {`<script src="https://call24x7.ai/widget.js" 
  data-agent-id="your-agent-id">
</script>`}
          </code>
        </div>
        <p>{t('blog.integrateAIWhatsApp.step3Outro')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.integrateAIWhatsApp.step4Title')}</h2>
        <p>{t('blog.integrateAIWhatsApp.step4Intro')}</p>
        <ul className="list-disc list-inside space-y-2 my-4">
          <li>{t('blog.integrateAIWhatsApp.step4Item1')}</li>
          <li>{t('blog.integrateAIWhatsApp.step4Item2')}</li>
          <li>{t('blog.integrateAIWhatsApp.step4Item3')}</li>
          <li>{t('blog.integrateAIWhatsApp.step4Item4')}</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.integrateAIWhatsApp.bestPracticesTitle')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.integrateAIWhatsApp.practice1Title')}</h3>
        <p>{t('blog.integrateAIWhatsApp.practice1Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.integrateAIWhatsApp.practice2Title')}</h3>
        <p>{t('blog.integrateAIWhatsApp.practice2Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.integrateAIWhatsApp.practice3Title')}</h3>
        <p>{t('blog.integrateAIWhatsApp.practice3Desc')}</p>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.integrateAIWhatsApp.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.integrateAIWhatsApp.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.integrateAIWhatsApp.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default IntegrateAIWhatsApp;
